import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QA = () => {
  const [faqs, setFaqs] = useState([]);
  const [siteInfo, setSiteInfo] = useState(null);
  const [pageContent, setPageContent] = useState(null);
  const [openIndex, setOpenIndex] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [faqsRes, siteInfoRes, pageRes] = await Promise.all([
        axios.get(`${API_URL}/faqs`),
        axios.get(`${API_URL}/site-info`),
        axios.get(`${API_URL}/page-content/qa`)
      ]);
      setFaqs(faqsRes.data);
      setSiteInfo(siteInfoRes.data);
      setPageContent(pageRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-sage-50 via-lavender-50 to-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6">
            <h1 className="text-5xl md:text-6xl font-serif text-gray-900">{pageContent?.title || 'Questions & Answers'}</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {pageContent?.description || 'Find answers to common questions about breast implant illness, explant surgery, and coaching'}
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card
                key={faq.id}
                className="border-2 border-sage-100 hover:border-sage-300 transition-all duration-300 shadow-sm hover:shadow-md overflow-hidden"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full text-left p-6 flex justify-between items-center"
                >
                  <h3 className="text-xl font-semibold text-gray-900 pr-8">{faq.question}</h3>
                  {openIndex === index ? (
                    <ChevronUp className="h-6 w-6 text-sage-700 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="h-6 w-6 text-sage-700 flex-shrink-0" />
                  )}
                </button>
                <div
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <CardContent className="px-6 pb-6">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-sage-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-3xl md:text-4xl font-serif text-gray-900">
            Have More Questions?
          </h2>
          <p className="text-lg text-gray-600">
            I'm here to help. Reach out through WhatsApp or schedule a call to discuss your specific situation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={siteInfo?.whatsapp || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-8 py-4 bg-sage-700 text-white rounded-xl hover:bg-sage-800 transition-all duration-300 hover:shadow-lg font-medium"
            >
              Contact on WhatsApp
            </a>
            <a
              href={`tel:${siteInfo?.phone || ''}`}
              className="inline-block px-8 py-4 border-2 border-sage-700 text-sage-700 rounded-xl hover:bg-sage-50 transition-all duration-300 font-medium"
            >
              Call {siteInfo?.phone || 'Loading...'}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default QA;